import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ErrorNote, Field, GoldButton, Sent, Strength, authErr } from "@/components/login/AuthBits";
import { cn } from "@/lib/utils";

const okEmail = (e) => /^\S+@\S+\.\S+$/.test(e.trim());

const Forgot = ({ email0, onBack }) => {
  const [email, setEmail] = useState(email0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const go = async (e) => {
    e.preventDefault(); setErr("");
    if (!okEmail(email)) return setErr("Enter the email you signed up with.");
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email: email.trim().toLowerCase(), reset_url: `${window.location.origin}/reset-password` }); setSent(true); }
    catch (x) { setErr(authErr(x)); } finally { setBusy(false); }
  };
  if (sent) return <Sent email={email.trim()} onBack={onBack} />;
  return (
    <form onSubmit={go} className="auth-in space-y-4" data-testid="auth-forgot" noValidate>
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 transition-colors hover:text-[#F3C649]" data-testid="auth-forgot-back"><ArrowLeft className="h-3.5 w-3.5" />Back</button>
      <div><p className="font-display text-lg text-zinc-50">Reset your password</p><p className="mt-1 text-sm text-zinc-400">We'll email you a link to set a new one. Signed up with Google before? This also lets you add a password to that account.</p></div>
      <Field label="Email" type="email" value={email} onChange={setEmail} testid="auth-forgot-email" autoComplete="email" autoFocus />
      <ErrorNote msg={err} />
      <GoldButton busy={busy} testid="auth-forgot-submit">Send reset link</GoldButton>
    </form>
  );
};

export const EmailAuth = ({ signInOnly }) => {
  const { loginWithPassword } = useAuth();
  const [mode, setMode] = useState("signin");
  const [v, setV] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (x) => { setV({ ...v, [k]: x }); setErr(""); };
  const up = mode === "signup";
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    const email = v.email.trim().toLowerCase();
    if (up && !v.name.trim()) return setErr("Please add your name.");
    if (!okEmail(email)) return setErr("Please enter a valid email address.");
    if (!v.password) return setErr("Please enter your password.");
    if (up && v.password.length < 8) return setErr("Use at least 8 characters for your password.");
    setBusy(true);
    try { await loginWithPassword(up ? "register" : "login", up ? { name: v.name.trim(), email, password: v.password } : { email, password: v.password }); }
    catch (x) { setErr(x?.response?.status === 409 ? "That email already has an account. Sign in instead, or reset your password if you used Google before." : authErr(x)); setBusy(false); }
  };
  if (mode === "forgot") return <Forgot email0={v.email} onBack={() => setMode("signin")} />;
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="auth-email-form" noValidate>
      {!signInOnly && <div className="grid grid-cols-2 rounded-xl border border-zinc-800 bg-[#09090B]/80 p-1" role="tablist">
        {[["signin", "Sign in"], ["signup", "Create account"]].map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => { setMode(k); setErr(""); }} className={cn("rounded-lg py-2 text-sm transition-[background-color,color,box-shadow] duration-200", mode === k ? "bg-zinc-800/90 font-semibold text-[#F3C649] shadow-[inset_0_0_0_1px_rgba(212,175,55,0.35)]" : "text-zinc-400 hover:text-zinc-200")} data-testid={`auth-tab-${k}`}>{l}</button>
        ))}
      </div>}
      <div key={mode} className="auth-in space-y-4">
        {up && <Field label="Full name" value={v.name} onChange={set("name")} testid="auth-name" autoComplete="name" />}
        <Field label="Email" type="email" value={v.email} onChange={set("email")} testid="auth-email" autoComplete="email" />
        <div>
          <Field label="Password" type="password" value={v.password} onChange={set("password")} testid="auth-password" autoComplete={up ? "new-password" : "current-password"}
            right={!up && <button type="button" onClick={() => setMode("forgot")} className="normal-case tracking-normal text-[#D4AF37] transition-colors hover:text-[#F3C649]" data-testid="auth-forgot-link">Forgot password?</button>} />
          {up && <Strength p={v.password} />}
        </div>
        <ErrorNote msg={err} />
        <GoldButton busy={busy} testid="auth-submit">{up ? "Create account" : "Sign in"}</GoldButton>
      </div>
    </form>
  );
};
